import {UserRoundCog} from 'lucide-react'
import React, {FC, useState} from 'react'
import {useIntl} from 'react-intl'

import type {AllocationServerGrid} from '../../types/allocationServer'

/**
 * A class subject a file names for one teacher while another teacher already has it
 * (`heldByOther`, AI server): the server keeps the current teacher and the page asks.
 */
export type HeldByOtherCell = {
  subjectId: number
  /** The teacher the file names for the cell. */
  teacherId: number
  /** The teacher who has the cell now. */
  holderId: number
  teacher?: string | null
  holder?: string | null
  class?: string | null
  subject?: string | null
}

/**
 * The prefill's `heldByOther` (ids only) with the names the page shows: teachers from the grid's
 * teacher list, the class and subject from the cell (a whole-school grid, no scope).
 */
export const heldCellsWithNames = (
  held: ReadonlyArray<{subjectId: number; teacherId: number; holderId: number}>,
  grid: AllocationServerGrid | null
): HeldByOtherCell[] => {
  const teachers = new Map((grid?.teachers ?? []).map((teacher) => [teacher.id, teacher.name]))
  const columns = new Map((grid?.columns ?? []).map((column) => [column.key, column.name]))
  const places = new Map<number, {class: string; subject: string}>()
  grid?.branches.forEach((branch) =>
    branch.rows.forEach((row) =>
      Object.entries(row.cells).forEach(([key, cell]) =>
        places.set(cell.subjectId, {
          class: row.displayName || row.name,
          subject: columns.get(key) ?? key,
        })
      )
    )
  )
  return held.map((cell) => ({
    ...cell,
    teacher: teachers.get(cell.teacherId) ?? null,
    holder: teachers.get(cell.holderId) ?? null,
    class: places.get(cell.subjectId)?.class ?? null,
    subject: places.get(cell.subjectId)?.subject ?? null,
  }))
}

type Props = {
  cells: HeldByOtherCell[]
  /** "Replace": the cell gets the file's teacher (PATCH the cell's teacher). Rejects on failure. */
  onReplace: (cell: HeldByOtherCell) => Promise<unknown>
}

/**
 * One question per cell another teacher already has: "Ali Khan teaches Maths in Grade 7-A; the
 * file says Sara Ahmed. Replace?" Nothing is replaced without a yes; Keep leaves the cell as it
 * is. An answered cell leaves the list.
 */
const HeldByOtherQuestions: FC<Props> = ({cells, onReplace}) => {
  const intl = useIntl()
  const [answered, setAnswered] = useState<ReadonlySet<number>>(new Set())
  const [busy, setBusy] = useState<number | null>(null)
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.HELD.${id}`}, values)

  const open = cells.filter((cell) => !answered.has(cell.subjectId))
  if (open.length === 0) return null
  const answer = (cell: HeldByOtherCell) =>
    setAnswered((prev) => new Set(prev).add(cell.subjectId))

  return (
    <section className='ta-card ta-held' aria-label={t('TITLE', {count: open.length})}>
      <p className='ta-held__title'>
        <UserRoundCog size={18} aria-hidden />
        {t('TITLE', {count: open.length})}
      </p>
      <ul className='ta-held__list'>
        {open.map((cell) => {
          const values = {
            holder: cell.holder?.trim() || t('SOMEONE'),
            teacher: cell.teacher?.trim() || t('SOMEONE'),
            subject: cell.subject?.trim() || '',
            class: cell.class?.trim() || '',
          }
          return (
            <li key={cell.subjectId} className='ta-held__item'>
              <p>{t(values.subject && values.class ? 'QUESTION' : 'QUESTION_NO_PLACE', values)}</p>
              <div className='ta-held__actions'>
                <button
                  type='button'
                  className='ta-btn ta-btn--primary ta-btn--sm'
                  disabled={busy !== null}
                  onClick={() => {
                    setBusy(cell.subjectId)
                    onReplace(cell)
                      .then(() => answer(cell))
                      // Refused: the question stays (the failure was said by the caller).
                      .catch(() => undefined)
                      .finally(() => setBusy(null))
                  }}
                >
                  {busy === cell.subjectId && (
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden />
                  )}
                  {t('REPLACE')}
                </button>
                <button
                  type='button'
                  className='ta-btn ta-btn--outline ta-btn--sm'
                  disabled={busy === cell.subjectId}
                  onClick={() => answer(cell)}
                >
                  {t('KEEP', {holder: values.holder})}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default HeldByOtherQuestions
